import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { ConfirmContext, type ConfirmOptions, type DismissConfirmation } from './ConfirmContext';

export const useConfirm = (scope?: unknown, enabled = true) => {
  const context = useContext(ConfirmContext);
  const eligibility = useRef(enabled); eligibility.current = enabled;
  const owned = useRef(new Set<DismissConfirmation>());
  const owner = useRef({ scope, generation: 0 });
  if (!Object.is(owner.current.scope, scope)) owner.current = { scope, generation: owner.current.generation + 1 };
  useEffect(() => {
    const handles = owned.current;
    return () => { for (const dismiss of handles) dismiss(); handles.clear(); };
  }, []);
  // Async consumers keep their original statement order. Cancelling, a rejected
  // duplicate, navigation purge or caller unmount never authorizes the action.
  const ask = useCallback((message: string, title?: string, options?: ConfirmOptions) => new Promise<boolean>(resolve => {
    const generation = owner.current.generation;
    const isCurrent = () => eligibility.current && owner.current.generation === generation && (options?.isCurrent?.() ?? true);
    let handle: DismissConfirmation | undefined;
    const settle = (accepted: boolean) => { if (handle) { owned.current.delete(handle); handle = undefined; } resolve(accepted && isCurrent()); };
    handle = context.confirm(message, () => settle(true), title, () => settle(false), { ...options, isCurrent });
    if (handle.accepted) owned.current.add(handle);
    else settle(false);
  }), [context.confirm]);
  const confirmAsync = useCallback((message: string, onOk: () => void | Promise<void>, title?: string,
    onCancel?: () => void, options?: ConfirmOptions) => {
    const generation = owner.current.generation;
    let handle: DismissConfirmation | undefined;
    const release = () => { if (handle) { owned.current.delete(handle); handle = undefined; } };
    handle = context.confirm(message, async () => { await onOk(); release(); }, title,
      () => { release(); onCancel?.(); }, { ...options, waitForCompletion: true,
        isCurrent: () => eligibility.current && owner.current.generation === generation && (options?.isCurrent?.() ?? true),
      });
    if (handle.accepted) owned.current.add(handle);
    return handle;
  }, [context.confirm]);
  return useMemo(() => ({ ...context, ask, confirmAsync }), [context, ask, confirmAsync]);
};
